import { cn } from "@client/utils/cn";

/**
 * The scroller a route owns.
 *
 * The app shell is viewport-locked (`h-dvh`, clipped — see
 * `appShellClassName.ts`): nothing scrolls at the document level, and every
 * route mounts one of these as the flex child that takes the space under the
 * top bar and scrolls its own content.
 *
 * `relative` is the load-bearing part, not decoration. An absolutely
 * positioned descendant — Tailwind's `sr-only`, Radix's hidden form inputs, an
 * icon pinned inside an input — resolves against its nearest POSITIONED
 * ancestor, and overflow clipping only applies to descendants whose containing
 * block sits inside the clipping element. With no positioned element between
 * such a descendant and `<body>`, it resolves against the viewport, escapes
 * both this scroller's clip and the shell's, and its static position (wherever
 * the content put it) becomes scrollable overflow on the document: the page
 * grows a second scrollbar and the shell slides up behind the fixed top bar.
 * That was the Application Builder's "double scroll" (its "(required)"
 * screen-reader labels), and it is why the pattern lives in one component
 * rather than a class string copied per route.
 */
export function RouteScroller({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      {...props}
      className={cn("relative min-h-0 flex-1 overflow-y-auto", className)}
    />
  );
}

/** Measure a route without CSS containment, which can leave WebKit content unpainted. */
export function measureRouteViewport(
  node: HTMLDivElement | null,
  property = "--route-viewport-height",
) {
  if (!node) return;
  const update = () =>
    node.style.setProperty(property, `${node.clientHeight}px`);
  update();
  const observer = new ResizeObserver(update);
  observer.observe(node);
  return () => {
    observer.disconnect();
    node.style.removeProperty(property);
  };
}
